import axios from 'axios';
import appConfig from '../config/appConfig';
import { apiRoutes } from './apiRoutes';

let refreshPromise = null;

const clearTokens = () => {
  localStorage.removeItem(appConfig.auth.accessTokenKey);
  localStorage.removeItem(appConfig.auth.refreshTokenKey);
};

const refreshAccessToken = () => {
  const refreshToken = localStorage.getItem(appConfig.auth.refreshTokenKey);

  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token is available.'));
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${appConfig.api.baseURL}${apiRoutes.auth.tokenRefresh}`, {
        refresh: refreshToken,
      })
      .then(({ data }) => {
        localStorage.setItem(appConfig.auth.accessTokenKey, data.access);

        // Supports refresh-token rotation if it is enabled on the backend later.
        if (data.refresh) {
          localStorage.setItem(appConfig.auth.refreshTokenKey, data.refresh);
        }

        return data.access;
      })
      .catch((error) => {
        // A rejected token is terminal. Keep tokens during a temporary network
        // failure so an outage does not unnecessarily sign the user out.
        if ([400, 401].includes(error.response?.status)) {
          clearTokens();
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

/**
 * Add JWT authentication and one automatic retry to an Axios instance.
 * Concurrent 401 responses share one refresh request.
 */
export const attachAuthSession = (client) => {
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(appConfig.auth.accessTokenKey);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        originalRequest._authRetry ||
        !localStorage.getItem(appConfig.auth.refreshTokenKey)
      ) {
        return Promise.reject(error);
      }

      originalRequest._authRetry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return client.request(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );

  return client;
};

export default attachAuthSession;
